import React, { useContext, useEffect, useRef, useState } from 'react';
import { connect, useSelector, useDispatch } from 'react-redux';
import SeatingChart from 'components/seatingChart/seatSelection/SeatingChart';
import { ModalContainer } from 'components/common/modals/ModalContainer';
import Flex from 'components/common/containers/Flex';
import { LoadingIcon } from 'components/utils/LoadingView';
import { ChartConst, Layout } from 'util/Constants';
import 'sass/components/seatingChart/SeatingChart.scss';
import seatingChartReducer from 'stores/calendar/SeatingChartStore';
import { useParams } from 'react-router-dom';
import SelectSeat from 'components/seatingChart/seatSelection/seat/SelectSeat';
import WebSocketClient from './websocket/WebSocketClient';
import { selectTemporarySeat } from 'components/seatingChart/actions';
import { getTemporarySelectedSeat } from 'components/seatingChart/selectors';
import 'sass/_global.scss';
import { getAllPromos } from './apis/promotions';
import { ReduxStore } from 'reducers/configureStore';
import { setPromos as setPromosRedux } from './slices/promoSlice';
import {
	selectFeatureFlag,
	setFeatureFlags as setFeatureFlagsRedux,
} from './slices/featureFlagSlice';
import { setPriceLevels } from './slices/priceLevelSlice';
import LandscapeMessage from './components/common/messages/LandscapeMessage';
import { LoadErrorMessageContext } from './components/seatingChart/context/LoadErrorMessageContext';
import { addToCart, unSelectSeat } from 'apis/cartWebSocketClient';
import { MultiselectToolbar } from 'components/seatingChart/MultiSelect/MultiselectToolbar';
import { patronHoldLoad } from './apis/cartWebSocketClient';
import { generateSeatObjectForAddToCart } from 'components/utils/handlers/seatingChartHandlers';
import {
	selectSelectedPromo,
	selectSelectedTicketRule,
	selectSelectedTicketType,
} from './slices/selectedMultiselectOptions';
import { getFeatureFlagsAll } from 'apis/featureFlagsApi';
import {updateIsMobile} from './slices/uiSlice';
import {
	attachInitListener,
	sendWidgetReady,
	attachCartResultListener,
	attachSeatDeselectedListener,
	sendAddToCart,
	sendRemoveSeat,
	sendPatronHoldLoad,
} from 'util/postMessageHandshake';

const { CHART_PADDING, TOOLBAR_HEIGHT } = ChartConst;

const App = (props) => {
	const { clientId, performanceId, productionId } = useParams();
	const [hasAccSeating, setHasAccSeating] = useState(false);
	const [seatingChart, setSeatingChart] = useState(
		props.seatingChart?.seatingChart || {},
	);
	const [selectedSeats, setSelectedSeats] = useState({});

	const [allSelectedSeats, setAllSelectedSeats] = useState({});
	const [allSelectedSeatsArray, setAllSelectedSeatsArray] = useState([]);
	const [coordinates, setCoordinates] = useState({});

	const [, setLoading] = useState(true);
	const [, setError] = useState(null);
	const [currencySymbol] = useState('$');
	const [promos, setPromos] = useState([]);
	const [websocketMessage, setWebsocketMessage] = useState(null);
	const [awaitingInit, setAwaitingInit] = useState(false);
	const [eventId, setEventId] = useState(null);
	const [originRejected, setOriginRejected] = useState(false);
	const [hostOrigin, setHostOrigin] = useState(null);

	const { errorMessage, setErrorMessage } = useContext(LoadErrorMessageContext);

	// requestId -> reconcile callback for cart requests still awaiting a host
	// response. Also invoked for a request that already timed out client-side
	// if the host's result arrives late, so a seat that's genuinely in the
	// cart doesn't get stuck showing as unselected/unselectable (#117787,
	// #117935).
	const pendingCartReconcileRef = useRef(new Map());

	const selectedMultiSelectOptions = useSelector(
		(state) => state.selectedMultiSelectOptions,
	);
	const selectedPromoId = selectedMultiSelectOptions.selectedPromoId;
	const selectedTicketTypeName =
		selectedMultiSelectOptions.selectedTicketTypeName;
	const isMultiSelectEnabled = useSelector(
		selectFeatureFlag('is-multi-select-enabled'),
	);
	const isPatronHoldSeatSelectEnabled = useSelector(
		selectFeatureFlag('is-patron-hold-seats-select-enabled'),
	);
	const featureFlags = useSelector((state) => state.featureFlag.featureFlags);
	const isHandshakeEnabled = useSelector(
		selectFeatureFlag('is-widget-postmessage-handshake'),
	);
	const isCartPostMessageFlag = useSelector(
		selectFeatureFlag('is-sc-widget-cart-postmessage'),
	);
	// is-sc-widget-cart-postmessage is inert (falls back to HTTP) unless the
	// postMessage handshake itself is also enabled.
	const isCartPostMessageEnabled = isHandshakeEnabled && isCartPostMessageFlag;

	const dispatch = useDispatch();
	useEffect(() => {
		if (!websocketMessage) return;
		if (!websocketMessage.seatIds || websocketMessage.seatIds.length === 0) {
			onClosePopup();
			updateSelectedSeats();
		}

		if (websocketMessage.seatIds && websocketMessage.seatIds.length > 0) {
			websocketMessage.seatIds.forEach((seatId) => {
				setSelectedSeats((prevState) => {
					const newState = { ...prevState };
					delete newState[seatId];
					return newState;
				});

				setAllSelectedSeatsArray((prevArray) =>
					prevArray.filter((seat) => seat.id !== seatId),
				);
			});
		}
	}, [websocketMessage]);

	const handleResize = () => {
		setWindowSize({
			width: window.innerWidth,
			height: window.innerHeight,
		});
		dispatch(updateIsMobile(window.innerWidth<600))
	};

	const fetchSeatingChart = async (featureFlags) => {
		try {
			const isSeatingChartAPIEnabled =
				featureFlags.find(
					(flagObj) => flagObj.flag === 'is-seating-chart-api-enabled',
				)?.value ?? false;

			const seatingChart = await seatingChartReducer.fetchSeatingChartData(
				performanceId,
				clientId,
				true,
				setErrorMessage,
				isSeatingChartAPIEnabled,
			);
			setSeatingChart(seatingChart);
			ReduxStore.dispatch(setPriceLevels(seatingChart.priceLevels));
			setLoading(false);
		} catch (error) {
			setErrorMessage('Failed to load seating chart');
			setLoading(false);
		}
	};

	const fetchPromos = async () => {
		try {
			const promos = await getAllPromos(performanceId);
			setPromos(promos);
			ReduxStore.dispatch(setPromosRedux(promoArrayToObject(promos)));
		} catch (error) {
			setError(error);
		}
	};

	// Runs a cart-mutation postMessage request and applies onSuccess both on a
	// normal (in-time) resolution and, if the request already timed out
	// client-side, on a late result reconciled via attachCartResultListener's
	// onLateResult - so a seat the host genuinely added/removed/held always
	// ends up reflected on the map, even if the widget's own timeout fired
	// first under a burst of rapid clicks (#117787, #117935).
	const sendReconcilableCartRequest = async (sendFn, payload, onSuccess) => {
		let requestId;

		const result = await sendFn(payload, hostOrigin, {
			onRequestId: (id) => {
				requestId = id;
				pendingCartReconcileRef.current.set(id, onSuccess);
			},
		});

		if (requestId) pendingCartReconcileRef.current.delete(requestId);
		onSuccess();

		return result;
	};

	const handleLateCartResult = (_type, requestId) => {
		const reconcile = pendingCartReconcileRef.current.get(requestId);
		if (!reconcile) return;

		pendingCartReconcileRef.current.delete(requestId);
		reconcile();
	};

	// A timed-out request the host later answered with *_ERROR was never fulfilled: nothing to reconcile, just. Forget it.
	const handleLateCartError = (_type, requestId) => {
		pendingCartReconcileRef.current.delete(requestId);
	};

	// Removes a host-deselected seat from local selection state. Mirrors the
	// STOMP-pushed removal logic in the websocketMessage effect above (the
	// SEAT_DESELECTED postMessage is the postMessage-mediator equivalent of
	// that same server-initiated removal).
	const handleSeatDeselected = (payload) => {
		const seatId = payload?.seatId;
		if (seatId == null) return;

		setSelectedSeats((prevState) => {
			const newState = { ...prevState };
			delete newState[seatId];
			return newState;
		});

		setAllSelectedSeatsArray((prevArray) =>
			prevArray.filter((seat) => seat.id !== seatId),
		);
	};

	useEffect(() => {
		setHasAccSeating(true);
		window.addEventListener('resize', handleResize);
		dispatch(updateIsMobile(window.innerWidth < 600));

		let removeInitListener = () => {};
		let removeCartResultListener = () => {};
		let removeSeatDeselectedListener = () => {};

		const fetchInitialData = async () => {
			try {
				let featureFlags = ReduxStore.getState().featureFlag.featureFlags;

				if (!featureFlags || Object.keys(featureFlags).length === 0) {
					featureFlags = await getFeatureFlagsAll();
					ReduxStore.dispatch(
						setFeatureFlagsRedux(featureFlagsToObject(featureFlags)),
					);
				}

				const isHandshakeEnabledLocal =
					featureFlags.find(
						(flagObj) => flagObj.flag === 'is-widget-postmessage-handshake',
					)?.value ?? false;
				const isCartPostMessageEnabledLocal =
					isHandshakeEnabledLocal &&
					(featureFlags.find(
						(flagObj) => flagObj.flag === 'is-sc-widget-cart-postmessage',
					)?.value ?? false);

				if (isHandshakeEnabledLocal) {
					setAwaitingInit(true);
					sendWidgetReady();
					removeInitListener = attachInitListener(
						(data, origin) => {
							setEventId(data?.eventId ?? null);
							setHostOrigin(origin);
							setOriginRejected(false);
							setAwaitingInit(false);
						},
						() => setOriginRejected(true),
					);

					if (isCartPostMessageEnabledLocal) {
						removeCartResultListener = attachCartResultListener(
							handleLateCartResult,
							handleLateCartError,
						);
						removeSeatDeselectedListener = attachSeatDeselectedListener(
							handleSeatDeselected,
						);
					}
				}

				fetchSeatingChart(featureFlags);
				fetchPromos();
			} catch (error) {
				setErrorMessage('Failed to load configuration');
			} finally {
				setLoading(false);
			}
		};

		fetchInitialData();

		return () => {
			window.removeEventListener('resize', handleResize);
			removeInitListener();
			removeCartResultListener();
			removeSeatDeselectedListener();
		};
	}, []);

	/**
	 * Clears the pending (grey) seat after a failed cart request, but only when
	 * it is still the seat that failed, so a later click on another seat is kept.
	 * @param {string} seatId id of the seat whose cart request failed
	 */
	const clearTemporarySeatIfStill = (seatId) => {
		const current = getTemporarySelectedSeat(ReduxStore.getState());
		if (current?.id === seatId) {
			props.onSelectTemporarySeat({});
		}
	};

	const onMultiSelect = async (data) => {
		const state = ReduxStore.getState();
		const promotion = selectSelectedPromo(state);
		const selectedTicketType = selectSelectedTicketType(data.priceLevel.id)(
			state,
		);
		const selectedTicketRule = selectSelectedTicketRule(
			selectedTicketType.ticketTypeId,
		)(state);
		const seatObj = {
			sectionId: data?.sectionId,
			selectedTicketTypeId: selectedTicketType.ticketTypeId,
			selectedTicketRuleId: selectedTicketRule?.id
				? selectedTicketRule?.id
				: null,
			promotionPrice: selectedTicketRule ? (selectedTicketRule.price + selectedTicketRule.facilityFeePrice) : 0,
			promotionBOFee: selectedTicketRule?.consumerFeeBO || 0,
			promotionName:
				promotion?.promotionName === 'Full price'
					? null
					: promotion?.promotionName || null,
			name: data?.sectionName,
			promotionCode: promotion?.promoCode ? promotion?.promoCode : null,
			promotionId:
				promotion?.promotionId === 'Full price'
					? null
					: promotion?.promotionId || null,
			seats: data.id,
			performanceId: performanceId,
			seatType: data?.priceLevel?.type,
			seatTypeInitial: 'available',
			type: 'MSG',
			number: data.number,
			row: data.row,
			rowNumber: data.rowNumber,
			selectedTicketName: selectedTicketType.name,
		};

		try {
			if (isCartPostMessageEnabled) {
				await sendReconcilableCartRequest(sendAddToCart, seatObj, () => {
					setSelectedSeats((prevState) => {
						return { ...prevState, [data.id]: data };
					});
					props.onSelectTemporarySeat({});
				});
			} else {
				const result = await addToCart(seatObj);
				if (result) {
					setSelectedSeats((prevState) => {
						return { ...prevState, [data.id]: data };
					});
					props.onSelectTemporarySeat({});
				} else {
					clearTemporarySeatIfStill(data.id);
				}
			}
		} catch (error) {
			console.error('Error handleAddToCart:', error);
			clearTemporarySeatIfStill(data.id);
		}
	};

	const onPatronHoldSeatSelect = async (data) => {
		try {
			const ticket = data?.ticket;
			if (!ticket) return;

			const patronHoldSeats =
				seatingChartReducer.extractSeatsByPatronIdAndSection(
					seatingChart,
					ticket.patron.id,
					data.sectionId,
				);

			const seatObjects = patronHoldSeats.map((seat) =>
				processPatronHoldSeatForAddToCart(
					seat,
					selectedTicketTypeName,
					selectedPromoId,
				),
			);

			if (isCartPostMessageEnabled) {
				const patronMessage = {
					patronId: ticket.patron.id,
					firstName: ticket.patron.firstName,
					lastName: ticket.patron.lastName,
					patronHoldSeats: seatObjects,
				};

				await sendReconcilableCartRequest(sendPatronHoldLoad, patronMessage, () => {
					setSelectedSeats((prevState) => ({
						...prevState,
						...Object.fromEntries(patronHoldSeats.map((seat) => [seat.id, seat])),
					}));

					props.onSelectTemporarySeat({});
				});
			} else {
				const result = await patronHoldLoad(ticket, seatObjects);
				if (!result) {
					clearTemporarySeatIfStill(data.id);
					return;
				}

				setSelectedSeats((prevState) => ({
					...prevState,
					...Object.fromEntries(patronHoldSeats.map((seat) => [seat.id, seat])),
				}));

				props.onSelectTemporarySeat({});
			}
		} catch (error) {
			console.error('Error validateHoldAndSendPatronLoadMessage:', error);
			clearTemporarySeatIfStill(data.id);
		}
	};

	const processPatronHoldSeatForAddToCart = (seat, ticketTypeName, promoId) => {
		const ticketTypes = seat.priceLevel.ticketTypes;
		const ticketType = ticketTypes.find(
			(ticketType) => ticketType.name === ticketTypeName,
		);
		let promotionSelected = null;
		if (promoId !== 'Full price') {
			const promotion = promos.find(
				(promotion) => promotion.promotionId === promoId,
			);
			const ticketRule = promotion.ticketRules.find(
				(ticketRule) => ticketRule.ticketTypeId === ticketType.ticketTypeId,
			);
			promotionSelected = {
				name: promotion.promotionName,
				promotionCode: promotion.promoCode,
				promotionId: promotion.promotionId,
				ticketRuleId: ticketRule.id,
				price: ticketRule.price,
				convenienceFeeBO: ticketRule.consumerFeeBO,
			};
		}
		let ticket = {
			ticketTypeId: ticketType.ticketTypeId,
			name: ticketType.name,
			promotionId: promotionSelected ? promotionSelected.promotionId : null,
			promotionCode: promotionSelected ? promotionSelected.promotionCode : null,
			selectedTicketRuleId: promotionSelected
				? promotionSelected.ticketRuleId
				: null,
		};
		const ticketForPricing = {
			promotionCode: null,
			promotionId: null,
		};
		return generateSeatObjectForAddToCart(
			seat,
			ticket,
			promotionSelected,
			ticketForPricing,
			performanceId,
		);
	};

	const onSeatSelect = (data, _seat, coordinates) => {
		let isPatronHoldSeatSelected =
			data.ticket?.patron && 'PATN' == data.ticket?.statusInitial;
		if (isMultiSelectEnabled) {
			if (isPatronHoldSeatSelected) {
				if (!isPatronHoldSeatSelectEnabled) return;
				onPatronHoldSeatSelect(data);
			} else {
				onMultiSelect(data);
			}
			return;
		}
		let allSelectedSeats = Object.assign(
			{},
			{ ...selectedSeats },
			{ [data.id]: data },
		);
		setAllSelectedSeatsArray((prevArray) => [...prevArray, data]);
		setAllSelectedSeats(allSelectedSeats);
		setCoordinates(coordinates);
	};

	const updateSelectedSeats = () => {
		setSelectedSeats(allSelectedSeats);
	};

	const onClosePopup = () => {
		setAllSelectedSeats({});
		setCoordinates([0, 0]);

		setAllSelectedSeatsArray([]);
		props.onSelectTemporarySeat({});
	};

	const onSeatUnselect = async (seats) => {
		const seatMessage = {
			type: 'removeSeat',
			seatIds: [seats?.id],
			performanceId: performanceId,
		};

		try {
			if (isCartPostMessageEnabled) {
				await sendReconcilableCartRequest(sendRemoveSeat, seatMessage, () => {
					setSelectedSeats((prevState) => {
						const newState = { ...prevState };
						delete newState[seats.id];
						return newState;
					});
				});
			} else {
				const response = await unSelectSeat(seatMessage);
				if (response) {
					setSelectedSeats((prevState) => {
						const newState = { ...prevState };
						delete newState[seats.id];
						return newState;
					});
				}
			}
		} catch (error) {
			console.error('Error onSeatUnselect:', error);
		}
	};

	// Encapsulates the flag-branching + send-or-HTTP logic for the single-seat
	// popup add-to-cart flow (SelectSeat.js), so SelectSeat itself doesn't need
	// to know about feature flags or postMessage.
	const onAddToCart = async (selectedSeat, selectedTicket, selectedPromo, ticketForPricing) => {
		const seatObj = generateSeatObjectForAddToCart(
			selectedSeat,
			selectedTicket,
			selectedPromo,
			ticketForPricing,
			performanceId,
		);

		try {
			if (isCartPostMessageEnabled) {
				await sendReconcilableCartRequest(sendAddToCart, seatObj, () => {
					updateSelectedSeats();
				});
			} else {
				const result = await addToCart(seatObj);
				if (result) updateSelectedSeats();
			}
		} catch (error) {
			console.error('Error handleAddToCart:', error);
		}
	};

	const [windowSize, setWindowSize] = useState({
		width: window.innerWidth,
		height: window.innerHeight,
	});

	const renderNoTicketsAvailableMessage = () => {
		const calculatedSCViewPortSize = [
			windowSize.width,
			windowSize.height - Layout.min_navbar_height,
		];

		const reducedWidth = calculatedSCViewPortSize[0] * 0.95;
		const reducedHeight = calculatedSCViewPortSize[1] * 0.95;

		if (errorMessage) {
			return (
				<LandscapeMessage
					toggleMessage={true}
					isPermanent={true}
					message={errorMessage}
					width={reducedWidth}
					height={reducedHeight}
				/>
			);
		}
	};

	const renderPermissionDeniedMessage = () => {
		const calculatedSCViewPortSize = [
			windowSize.width,
			windowSize.height - Layout.min_navbar_height,
		];

		const reducedWidth = calculatedSCViewPortSize[0] * 0.95;
		const reducedHeight = calculatedSCViewPortSize[1] * 0.95;

		return (
			<LandscapeMessage
				toggleMessage={true}
				isPermanent={true}
				message='You do not have permission to view this seating chart.'
				width={reducedWidth}
				height={reducedHeight}
			/>
		);
	};

	const renderSeatingChart = () => {
		if (Object.keys(seatingChart).length === 0 || awaitingInit) {
			return (
				<ModalContainer key='modalContainer' clear>
					<LoadingIcon key='loading' size='lg' />
				</ModalContainer>
			);
		}

		const quantity = selectedSeats && Object.keys(selectedSeats).length;
		const calculatedSCViewPortSize = [
			window.innerWidth,
			window.innerHeight - Layout.min_navbar_height,
		];

		const selectedSeat =
			allSelectedSeatsArray[allSelectedSeatsArray.length - 1];

		return (
			<React.Fragment>
				<Flex
					className='containerSeatingChart'
					columns={2}
					fit={true}
					isDisabled={false}
					stretch
				>
					<SeatingChart
						key='seating_chart'
						clientId={clientId}
						data={seatingChart}
						disableOldWorkFlow={false}
						displayPriceLevelFilter={false}
						displayZoomButtons={true}
						hasAccSeating={hasAccSeating}
						onClickBack={() => {}}
						isDragging={() => {}}
						onSeatSelect={onSeatSelect}
						onSeatUnselect={
							isMultiSelectEnabled ? onSeatUnselect : onClosePopup
						}
						onSelectCartSeat={() => {}}
						performanceId={performanceId}
						productionId={productionId}
						productionSeatSelectionMethod={null}
						quantity={quantity}
						query={null}
						selectedSeats={selectedSeats}
						hidePopup={() => {}}
						noToolBar={true}
						isSmallDevice={false}
						isMobile={false}
						padding={{
							top: TOOLBAR_HEIGHT,
							right: CHART_PADDING,
							bottom: CHART_PADDING,
							left: CHART_PADDING,
						}}
						viewPortSize={calculatedSCViewPortSize}
						currencySymbol={currencySymbol}
						featureFlags={featureFlags}
					/>
				</Flex>
				{Object.keys(allSelectedSeats).length > 0 && !isMultiSelectEnabled && (
					<SelectSeat
						coordinates={coordinates}
						selectedSeat={selectedSeat}
						promoName=''
						promos={promos}
						onClosePopup={onClosePopup}
						onAddToCart={onAddToCart}
					/>
				)}
			</React.Fragment>
		);
	};

	return (
		<div
			className='ot_performanceSeatingChartView'
			style={{ overflow: 'hidden' }}
			data-event-id={eventId || undefined}
		>
			{isMultiSelectEnabled && !awaitingInit && <MultiselectToolbar />}
			<React.Fragment>
				{errorMessage
					? renderNoTicketsAvailableMessage()
					: originRejected
					? renderPermissionDeniedMessage()
					: renderSeatingChart()}
				<WebSocketClient onMessage={setWebsocketMessage} />
			</React.Fragment>
		</div>
	);
};

const mapDispatchToProps = (dispatch) => ({
	onSelectTemporarySeat: (seat) => dispatch(selectTemporarySeat(seat)),
});

export default connect(null, mapDispatchToProps)(App);

// converts an array of promo objects to a promo object with the promoId as the key
function promoArrayToObject(promos) {
	const promoObj = {};
	promos.forEach((promo) => {
		promoObj[promo.promotionId] = promo;
	});
	return promoObj;
}

// convert an array of feature flags to a map
function featureFlagsToObject(featureFlags) {
	const featureFlagsObj = {};
	featureFlags.forEach((flag) => {
		featureFlagsObj[flag.flag] = flag.value;
	});
	return featureFlagsObj;
}
